import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, CircularProgress,
  Alert, Autocomplete, Divider, Typography, Box, InputAdornment,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  QrCode as QrCodeIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import axios from 'axios';

const sanitize = (s, max = 250) =>
  (s || '')
    .replace(/\r\n|\r|\n/g, ' ')
    .replace(/[\x00-\x1F\x7F]+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim()
    .substring(0, max);

const buildInitialForm = (nombre = '') => ({
  codigo: '',
  nombre,
  descripcion: nombre,
  categoria_id: '',
  subcategoria_id: '',
  marca_id: '',
  modelo_id: '',
  talla_id: '',
  color_id: '',
  unidad_medida_id: '',
  precio: '',
  estado: 'ACTIVO',
});

export default function ProductoRapidoDialog({ open, onClose, onProductoCreado, initialNombre = '' }) {
  const [form, setForm]       = useState(buildInitialForm());
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const [categorias, setCategorias]       = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [marcas, setMarcas]               = useState([]);
  const [modelos, setModelos]             = useState([]);
  const [tallas, setTallas]               = useState([]);
  const [colores, setColores]             = useState([]);
  const [unidades, setUnidades]           = useState([]);

  const subsFiltradas = useMemo(
    () => form.categoria_id
      ? subcategorias.filter(s => String(s.categoria_id) === String(form.categoria_id))
      : subcategorias,
    [subcategorias, form.categoria_id]
  );

  const modelosFiltrados = useMemo(
    () => form.marca_id
      ? modelos.filter(m => String(m.marca_id) === String(form.marca_id))
      : modelos,
    [modelos, form.marca_id]
  );

  useEffect(() => {
    if (open) {
      setForm(buildInitialForm(initialNombre));
      setError('');
      Promise.all([
        axios.get('/api/categorias'),
        axios.get('/api/subcategorias'),
        axios.get('/api/marcas'),
        axios.get('/api/modelos'),
        axios.get('/api/tallas'),
        axios.get('/api/colores'),
        axios.get('/api/unidades'),
      ]).then(([cat, sub, mar, mod, tal, col, uni]) => {
        setCategorias(Array.isArray(cat.data) ? cat.data : []);
        setSubcategorias(Array.isArray(sub.data) ? sub.data : []);
        setMarcas(Array.isArray(mar.data) ? mar.data : []);
        setModelos(Array.isArray(mod.data) ? mod.data : []);
        setTallas(Array.isArray(tal.data) ? tal.data : []);
        setColores(Array.isArray(col.data) ? col.data : []);
        setUnidades(Array.isArray(uni.data) ? uni.data : []);
      }).catch(() => {});
    }
  }, [open]); // eslint-disable-line

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => {
      const next = { ...f, [name]: value };
      if (name === 'nombre' && f.descripcion === f.nombre) {
        next.descripcion = value;
      }
      return next;
    });
  };

  const handleGuardar = async () => {
    if (!form.nombre?.trim()) { setError('El nombre del producto es obligatorio.'); return; }
    const precioNum = parseFloat(form.precio);
    if (form.precio === '' || isNaN(precioNum) || precioNum < 0) {
      setError('El precio debe ser un número mayor o igual a 0.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = {
        nombre:           sanitize(form.nombre, 250),
        descripcion:      sanitize(form.descripcion || form.nombre, 250),
        codigo:           form.codigo?.trim() || null,
        categoria_id:     form.categoria_id     || null,
        subcategoria_id:  form.subcategoria_id  || null,
        marca_id:         form.marca_id         || null,
        modelo_id:        form.modelo_id        || null,
        talla_id:         form.talla_id         || null,
        color_id:         form.color_id         || null,
        unidad_medida_id: form.unidad_medida_id || null,
        precio:           precioNum,
        estado:           form.estado === 'ACTIVO',
      };
      const res = await axios.post('/api/productos', payload);
      onProductoCreado(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detalle || 'Error al guardar el producto.');
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <InventoryIcon color="primary" />
        <Typography variant="h6" component="span" sx={{ flexGrow: 1 }}>
          Registrar nuevo producto
        </Typography>
        <Button size="small" onClick={onClose} disabled={loading} sx={{ minWidth: 0, p: 0.5 }}>
          <CloseIcon fontSize="small" />
        </Button>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>{error}</Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

          {/* Código */}
          <TextField
            fullWidth size="small"
            label="Código"
            name="codigo"
            value={form.codigo}
            onChange={handleChange}
            helperText="Vacío = auto-generar"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <QrCodeIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />

          {/* Nombre */}
          <TextField
            fullWidth size="small" required
            label="Nombre del producto"
            name="nombre"
            value={form.nombre}
            onChange={handleChange}
          />

          {/* Descripción */}
          <TextField
            fullWidth size="small"
            label="Descripción"
            name="descripcion"
            value={form.descripcion}
            onChange={handleChange}
            multiline
            rows={2}
            inputProps={{ maxLength: 250 }}
            helperText={`${(form.descripcion || '').length}/250 — los saltos de línea se convierten en espacio al guardar`}
          />

          {/* Categoría | Subcategoría */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <Box sx={{ width: '16em', minWidth: '16em' }}>
              <Autocomplete
                options={categorias}
                getOptionLabel={o => o.nombre || ''}
                value={categorias.find(c => c.id === form.categoria_id) || null}
                onChange={(_, v) => setForm(f => ({ ...f, categoria_id: v ? v.id : '', subcategoria_id: '' }))}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                size="small"
                renderInput={params => (
                  <TextField {...params} label="Categoría" size="small" fullWidth />
                )}
              />
            </Box>
            <Box sx={{ width: '16em', minWidth: '16em' }}>
              <Autocomplete
                options={subsFiltradas}
                getOptionLabel={o => o.nombre || ''}
                value={subsFiltradas.find(s => s.id === form.subcategoria_id) || null}
                onChange={(_, v) => setForm(f => ({ ...f, subcategoria_id: v ? v.id : '' }))}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                size="small"
                renderInput={params => (
                  <TextField {...params} label="Subcategoría" size="small" fullWidth />
                )}
              />
            </Box>
          </Box>

          {/* Marca | Modelo */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <Box sx={{ width: '16em', minWidth: '16em' }}>
              <Autocomplete
                options={marcas}
                getOptionLabel={o => o.nombre || ''}
                value={marcas.find(m => m.id === form.marca_id) || null}
                onChange={(_, v) => setForm(f => ({ ...f, marca_id: v ? v.id : '', modelo_id: '' }))}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                size="small"
                renderInput={params => (
                  <TextField {...params} label="Marca" size="small" fullWidth />
                )}
              />
            </Box>
            <Box sx={{ width: '16em', minWidth: '16em' }}>
              <Autocomplete
                options={modelosFiltrados}
                getOptionLabel={o => o.nombre || ''}
                value={modelosFiltrados.find(m => m.id === form.modelo_id) || null}
                onChange={(_, v) => setForm(f => ({ ...f, modelo_id: v ? v.id : '' }))}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                size="small"
                renderInput={params => (
                  <TextField {...params} label="Modelo" size="small" fullWidth />
                )}
              />
            </Box>
          </Box>

          {/* Talla | Color */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <Box sx={{ width: '16em', minWidth: '16em' }}>
              <Autocomplete
                options={tallas}
                getOptionLabel={o => o.nombre || ''}
                value={tallas.find(t => t.id === form.talla_id) || null}
                onChange={(_, v) => setForm(f => ({ ...f, talla_id: v ? v.id : '' }))}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                size="small"
                renderInput={params => (
                  <TextField {...params} label="Talla" size="small" fullWidth />
                )}
              />
            </Box>
            <Box sx={{ width: '16em', minWidth: '16em' }}>
              <Autocomplete
                options={colores}
                getOptionLabel={o => o.nombre || ''}
                value={colores.find(c => c.id === form.color_id) || null}
                onChange={(_, v) => setForm(f => ({ ...f, color_id: v ? v.id : '' }))}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                size="small"
                renderInput={params => (
                  <TextField {...params} label="Color" size="small" fullWidth />
                )}
              />
            </Box>
          </Box>

          {/* Unidad de Medida */}
          <Box sx={{ width: '14em', minWidth: '14em' }}>
            <Autocomplete
              options={unidades}
              getOptionLabel={o => o.nombre ? (o.nombre + ' (' + (o.abreviatura || '') + ')') : ''}
              value={unidades.find(u => u.id === form.unidad_medida_id) || null}
              onChange={(_, v) => setForm(f => ({ ...f, unidad_medida_id: v ? v.id : '' }))}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              size="small"
              renderInput={params => (
                <TextField {...params} label="Unidad de Medida" size="small" fullWidth />
              )}
            />
          </Box>

          {/* Precio | Estado */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <Box sx={{ width: '14em', minWidth: '14em' }}>
              <TextField
                fullWidth size="small" required
                label="Precio de venta"
                name="precio"
                value={form.precio}
                onChange={handleChange}
                type="number"
                inputProps={{ min: 0, step: '0.01' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoneyIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ width: '12em', minWidth: '12em' }}>
              <TextField
                select fullWidth size="small"
                label="Estado"
                name="estado"
                value={form.estado}
                onChange={handleChange}
              >
                <MenuItem value="ACTIVO">Activo</MenuItem>
                <MenuItem value="INACTIVO">Inactivo</MenuItem>
              </TextField>
            </Box>
          </Box>

        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="caption" color="text.secondary">* Campos obligatorios</Typography>
        </Box>
        <Button
          onClick={onClose}
          disabled={loading}
          startIcon={<CloseIcon />}
          color="inherit"
        >
          Cancelar
        </Button>
        <Button
          onClick={handleGuardar}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        >
          {loading ? 'Guardando...' : 'Guardar Producto'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
